import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home } from 'lucide-react';
import { toast } from 'sonner';

const BookingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    // Show success toast
    toast.success('🎉 Safari Booked Successfully!', {
      description: 'Your booking has been confirmed. Check your email for details.',
      duration: 5000,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="bg-emerald-100 rounded-full p-6 animate-bounce">
              <CheckCircle size={64} className="text-emerald-600" />
            </div>
          </div>

          {/* Success Message */}
          <h1 className="font-heading text-3xl md:text-4xl text-navy font-bold mb-4">
            🎉 Safari Booked Successfully!
          </h1>
          
          <p className="font-body text-lg text-gray-600 mb-8">
            Thank you for booking with <span className="font-bold text-emerald-600">JMD WILDLIFE</span>!
            <br />
            Your adventure awaits at Tadoba Tiger Reserve.
          </p>

          {/* Booking Details */}
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 mb-8">
            <h2 className="font-heading text-xl text-navy font-semibold mb-4">
              What's Next?
            </h2>
            <ul className="text-left space-y-3 font-body text-gray-700">
              <li className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-emerald-600 mt-1 flex-shrink-0" />
                <span>
                  <strong>Confirmation Email:</strong> Check your inbox for booking details and receipt
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-emerald-600 mt-1 flex-shrink-0" />
                <span>
                  <strong>Contact Us:</strong> We'll reach out within 24 hours to confirm your safari timing
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-emerald-600 mt-1 flex-shrink-0" />
                <span>
                  <strong>Prepare:</strong> Get ready for an unforgettable wildlife experience!
                </span>
              </li>
            </ul>
          </div>

          {/* Booking Reference */}
          {bookingId && (
            <div className="bg-gray-50 rounded-lg p-4 mb-8">
              <p className="text-sm text-gray-600 mb-1">Booking Reference:</p>
              <p className="font-mono text-lg font-bold text-navy">{bookingId}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center justify-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-lg"
            >
              <Home size={20} />
              <span>Back to Home</span>
            </button>
            
            <button
              onClick={() => navigate('/packages')}
              className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-50 text-emerald-600 border-2 border-emerald-600 px-8 py-4 rounded-full font-bold transition-all hover:scale-105"
            >
              <span>Explore More Safaris</span>
              <ArrowRight size={20} />
            </button>
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="font-body text-sm text-gray-600 mb-2">
              Questions? Contact us:
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center text-sm">
              <a 
                href="tel:+917776828551" 
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                📞 +91 7776828551
              </a>
              <span className="hidden md:inline text-gray-400">|</span>
              <a 
                href="mailto:jmdofficials08@gmail.com" 
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                ✉️ jmdofficials08@gmail.com
              </a>
              <span className="hidden md:inline text-gray-400">|</span>
              <a 
                href="https://wa.me/917776828551" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                💬 WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Looking forward to hosting you at Tadoba! 🐯
        </p>
      </div>
    </div>
  );
};

export default BookingSuccess;
